import { useEffect, useRef, type RefObject } from 'react';
import { type TextInput } from 'react-native';

import { type FieldClaimDisplay } from './fieldClaimDisplay';
import { useTemplateFieldPresence } from './useTemplateFieldPresence';

export interface ClaimableField {
  /** Every displayed claim on the field, earliest first; empty if unclaimed. */
  claims: FieldClaimDisplay[];
  /** Whether the claim currently blocks local editing. */
  blocked: boolean;
  /** Attach to the input so an override can focus it. */
  inputRef: RefObject<TextInput | null>;
  /** Call from the input's onFocus / onBlur. */
  focused(): void;
  blurred(): void;
  /**
   * Call when a press removes the field's input: it unmounts without a blur
   * event (native never blurs on the press), so release any held claim here.
   */
  inputRemoved(): void;
  /**
   * A tap landed on the blocked field: raise the haptic + toast. `open`
   * mounts a not-yet-rendered draft input (an add-affordance) before the
   * override focuses it.
   */
  blockedPress(open?: () => void): void;
}

/** One claimable field's presence wiring: claim, gating, and the input ref. */
export function useClaimableField(fieldKey: string): ClaimableField {
  const presence = useTemplateFieldPresence();
  const inputRef = useRef<TextInput>(null);

  // Release the claim on unmount: React fires no blur when the focused input
  // leaves the tree (e.g. a collaborator deletes the step being edited), so a
  // held claim would stick until the whole channel is torn down. The context
  // value's identity churns with every claims update, so the latest handler
  // lives in a ref and the cleanup runs only on unmount; fieldBlurred's
  // focused-field guard makes the call a no-op for fields that weren't held.
  const releaseRef = useRef(() => {});
  useEffect(() => {
    releaseRef.current = () => presence.fieldBlurred(fieldKey);
  });
  useEffect(() => () => releaseRef.current(), []);

  return {
    claims: presence.claimsFor(fieldKey),
    blocked: presence.isBlocked(fieldKey),
    inputRef,
    focused: () => presence.fieldFocused(fieldKey),
    blurred: () => presence.fieldBlurred(fieldKey),
    // fieldBlurred's focused-field guard makes this a no-op when the local
    // user didn't hold the removed input.
    inputRemoved: () => presence.fieldBlurred(fieldKey),
    blockedPress: (open?: () => void) =>
      presence.editBlockedPress(fieldKey, {
        focus: () => inputRef.current?.focus(),
        open,
      }),
  };
}
